/**
 * reportChartGrid — the SINGLE source for chart GRID + AXIS chrome colours,
 * derived from the report theme so the recharts CartesianGrid / ReferenceLine
 * read against the page background instead of a hardcoded cool-grey.
 *
 * RC (report-layout-rebuild W1, item 6): `ScoreDriverWaterfall` and
 * `ClimateProfileChart` hardcoded `#e5e7eb` (grid) and `#666` (axis/reference
 * line). On a warm-paper preset (journal `#fbfaf6`) or a near-white monograph
 * (`#fcfcfb`) those cool greys read as a foreign cool tint floating over warm
 * paper. This resolver derives the grid/axis ink from the theme's neutral ramp
 * (`roleColor` 'hair'/'rule') so the grid picks up the page's warmth.
 *
 * BYTE-STABILITY: for the DEFAULT (white `#ffffff`) background — i.e. legacy /
 * no-preset reports — this returns the EXACT historical literals (`#e5e7eb`
 * grid, `#666` axis), so those reports render pixel-identically. Only a tinted
 * background (a preset or a tenant paper colour) shifts the grid/axis ink.
 *
 * Pure / side-effect-free so it is safe in render and unit-testable.
 */
import type { ReportTheme } from '../types/reportTheme';
import { DEFAULT_THEME } from '../types/reportTheme';
import { roleColor } from './reportRoleColors';

/** The historical hardcoded chart-grid line colour. */
export const LEGACY_CHART_GRID = '#e5e7eb';
/** The historical hardcoded chart axis / reference-line colour. */
export const LEGACY_CHART_AXIS = '#666';

export interface ResolvedChartGridChrome {
  /** Stroke for `CartesianGrid` lines. */
  grid: string;
  /** Stroke for axis / `ReferenceLine` rules. */
  axis: string;
}

function normalizeHex(c: string | undefined | null): string | null {
  if (typeof c !== 'string') return null;
  const m = c.trim().toLowerCase().replace(/^#/, '');
  if (m.length === 3) return m.split('').map((ch) => ch + ch).join('');
  if (/^[0-9a-f]{6}$/.test(m)) return m;
  return null;
}

/**
 * Resolve the grid + axis colours for a chart on the given theme.
 *
 * When the theme background is the default page white (`#ffffff`) the result is
 * byte-identical to the legacy literals; any other (tinted) background derives
 * the grid from the theme's neutral `hair` wash and the axis from the stronger
 * `rule` wash, so the grid reads against the actual paper.
 */
export function resolveChartGridChrome(
  theme: Pick<ReportTheme, 'backgroundColor' | 'headingColor' | 'bodyTextColor'> | null | undefined,
): ResolvedChartGridChrome {
  const bg = normalizeHex(theme?.backgroundColor);
  const defaultBg = normalizeHex(DEFAULT_THEME.backgroundColor);
  // Default white page (legacy / no-preset) ⇒ exact historical literals.
  if (bg == null || bg === defaultBg) {
    return { grid: LEGACY_CHART_GRID, axis: LEGACY_CHART_AXIS };
  }
  // Tinted paper ⇒ derive from the theme's neutral ramp so the grid/axis ink
  // matches the page (warm paper → warm grid). 'hair' is the faint structural
  // wash; 'rule' is the stronger structural rule (axis).
  return {
    grid: roleColor(theme, 'hair'),
    axis: roleColor(theme, 'rule'),
  };
}
